// components/PropertyReport.jsx
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { pdf } from '@react-pdf/renderer';
import { useLanguage } from '../contexts/LanguageContext';
import KeyIndicators from './sections/KeyIndicators';
import FinancialSummary from './sections/FinancialSummary';
import FinancingSummary from './sections/FinancingSummary';
// Masqué à la demande — réactiver si besoin
// import ExecutiveSummary from './sections/ExecutiveSummary';
// import DealSummary from './sections/DealSummary';
// import SchlComplianceSection from './sections/SchlComplianceSection';
import ScenarioList from './ScenarioList';
import FutureScenarioForm from './FutureScenarioForm';
import RenewScenarioForm from './RenewScenarioForm';
import OptimisationScenarioForm from './OptimisationScenarioForm';
import calculateRentability from '../utils/calculateRentability';
import calculateReturnAfterYears from '../utils/calculateReturnAfterYears';
import FutureReturnsSection from './sections/FutureReturnsSection';
import {
  DEFAULT_RETURN_ASSUMPTIONS,
  resolveAssumptionRates,
  resolveAdvancedYearProfile,
  computeDisplayHints,
  assumptionsFromRefinancingSubScenario,
  buildAssumptionSummaryLines,
  buildAdvancedSchedulePdfRows,
  createEmptyAdvancedSchedule,
  resizeAdvancedSchedule,
  buildAdvancedScheduleFromSimple,
} from '../utils/returnHypothesisMath';
import { saveScenario, deleteScenario } from '../services/dataService';
import { saveShareScenario, deleteShareScenario } from '../services/shareService';
import calculateOptimisationScenario from '../utils/calculateOptimisationScenario';
import calculateRenewScenario from '../utils/calculateRenewScenario';
import calculateFutureScenario from '../utils/calculateFutureScenario';
import { getScenarios, getScenario } from '../services/dataService';
import { getShareScenarios, getShare, getShareScenariosOnce } from '../services/shareService';
import { getMarketParamsById } from '../services/marketParamsService';
import { RentalizerPdfDocument } from '../pdf/RentalizerPdfDocument';
import { resolveImageForPdf } from '../utils/resolveImageForPdf';
import { getPrimaryBuildingImageUrl } from '../utils/propertyImages';
import { useAuth } from '../contexts/AuthContext';
import { useRegisterTopEscapeClose } from '../hooks/useEscapeCloseStack';
import {
  isOwnedDeal,
  isInPlaceLoanFinancing,
  isZeroDebtFinancing,
} from '../utils/dealPurpose';

function getSubScenarioPdfBundle(sub, currentProperty, scenario, subScenarios, advancedExpenses) {
  if (!sub) return null;
  if (sub.type === 'refinancing') {
    const parentScenario =
      scenario?.id === sub.parentScenarioId
        ? scenario
        : subScenarios.find((s) => s.id === sub.parentScenarioId);
    const { analysis, equityWithdrawal, analysisProperty } =
      calculateFutureScenario(
        sub,
        currentProperty,
        parentScenario,
        advancedExpenses,
      );
    if (!analysis) return null;
    return {
      analysis,
      equityWithdrawal,
      property: analysisProperty
        ? {
            ...analysisProperty,
            ...sub.financing,
            ...sub.acquisitionCosts,
          }
        : {
            ...currentProperty,
            ...sub.financing,
            ...sub.acquisitionCosts,
          },
    };
  }
  if (sub.type === 'optimization') {
    const parentScenario =
      (scenario &&
        (scenario.id === sub.parentScenarioId ||
          scenario.parentScenarioId === sub.parentScenarioId))
        ? scenario
        : subScenarios.find((sc) => sc.id === sub.parentScenarioId);
    const { analysis, equityWithdrawal, analysisProperty } =
      calculateOptimisationScenario(
        sub,
        currentProperty,
        parentScenario,
        advancedExpenses,
      );
    if (!analysis) return null;
    return {
      analysis,
      equityWithdrawal,
      property: analysisProperty
        ? {
            ...analysisProperty,
            ...sub.financing,
            ...sub.acquisitionCosts,
          }
        : {
            ...currentProperty,
            ...sub.financing,
            ...sub.acquisitionCosts,
          },
    };
  }
  if (sub.type === 'renewal') {
    const parentScenario =
      scenario?.id === sub.parentScenarioId
        ? scenario
        : subScenarios.find((s) => s.id === sub.parentScenarioId);
    const { analysis, analysisProperty, combinedFinancing } = calculateRenewScenario(
      sub,
      currentProperty,
      parentScenario,
      advancedExpenses,
    );
    return {
      analysis,
      equityWithdrawal: null,
      property: { ...analysisProperty, ...combinedFinancing },
    };
  }
  return null;
}

function subScenarioGrowthStrings(sub, formatPercentFn) {
  if (!sub || sub.type !== 'refinancing') {
    return { income: '—', expense: '—', value: '—' };
  }
  return {
    income: formatPercentFn(parseFloat(sub.revenueGrowthPct) || 0),
    expense: formatPercentFn(parseFloat(sub.expenseGrowthPct) || 0),
    value: formatPercentFn(parseFloat(sub.valueAppreciationPct) || 0),
  };
}

const PropertyReport = ({
  currentProperty,
  setCurrentStep,
  analysis: baseAnalysis,
  onSave,
  advancedExpenses,
  scenario,
  onViewAmortization,
  shareToken = null,
  shareFilterByCreatorUid = null,
  baseScenarios: baseScenariosProp = null,
  shareCreatorInfo = null,
  sharedScenariosFromClients = null,
  readOnly = false,
  allowSubScenariosEdit = true,
}) => {
  const { t, language } = useLanguage();
  const { currentUser, userProfile } = useAuth();
  const [marketParamsVersion, setMarketParamsVersion] = useState(null);

  const isPropertyOwner = currentUser?.uid === currentProperty?.uid;
  const isBrokerViewingClientProperty =
    currentProperty?.brokerUid === currentUser?.uid && currentProperty?.uid !== currentUser?.uid;
  const canEditScenario = (sc) =>
    !sc.shareToken && (isPropertyOwner || (isBrokerViewingClientProperty && sc.createdByUid === currentUser?.uid));
  const canDeleteScenario = (sc) =>
    isPropertyOwner || (isBrokerViewingClientProperty && sc.createdByUid === currentUser?.uid);
  const [expandedClientScenario, setExpandedClientScenario] = useState(null);
  const [clientScenarioShareData, setClientScenarioShareData] = useState(null);
  const [parentScenarioForRenewal, setParentScenarioForRenewal] = useState(null);

  useEffect(() => {
    const id = userProfile?.selectedMarketParamsId;
    if (!id) {
      setMarketParamsVersion(null);
      return;
    }
    getMarketParamsById(id)
      .then((v) => setMarketParamsVersion(v ? { label: v.label, createdAt: v.createdAt } : null))
      .catch(() => setMarketParamsVersion(null));
  }, [userProfile?.selectedMarketParamsId]);

  useEffect(() => {
    if (!expandedClientScenario?.shareToken) {
      setClientScenarioShareData(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      getShare(expandedClientScenario.shareToken),
      getShareScenariosOnce(expandedClientScenario.shareToken),
    ])
      .then(([shareData, shareScenarios]) => {
        if (cancelled || !shareData?.snapshot) return;
        const base = shareData.snapshot.scenarios || [];
        setClientScenarioShareData({
          property: shareData.snapshot.property || currentProperty,
          baseScenarios: base,
          shareScenarios: shareScenarios || [],
        });
      })
      .catch(() => {
        if (!cancelled) setClientScenarioShareData(null);
      });
    return () => { cancelled = true; };
  }, [expandedClientScenario?.shareToken, expandedClientScenario?.id, currentProperty]);

  useEffect(() => {
    if (!scenario || scenario.type !== 'renewal' || !scenario.parentScenarioId) {
      setParentScenarioForRenewal(null);
      return;
    }
    if (shareToken) {
      setParentScenarioForRenewal(null);
      return;
    }
    let cancelled = false;
    if (scenario.shareToken) {
      getShareScenariosOnce(scenario.shareToken)
        .then((shareScenarios) => {
          if (cancelled) return;
          const parent = shareScenarios.find((s) => s.id === scenario.parentScenarioId) || null;
          setParentScenarioForRenewal(parent);
        })
        .catch(() => {
          if (!cancelled) setParentScenarioForRenewal(null);
        });
    } else if (currentProperty?.id) {
      getScenario(currentProperty.id, scenario.parentScenarioId)
        .then((parent) => {
          if (!cancelled) setParentScenarioForRenewal(parent);
        })
        .catch(() => {
          if (!cancelled) setParentScenarioForRenewal(null);
        });
    }
    return () => { cancelled = true; };
  }, [currentProperty?.id, scenario, shareToken]);

  const typeLabels = {
    refinancing: t('scenario.refinancing'),
    renewal: t('scenario.renewal'),
    optimization: t('scenario.optimization'),
    other: t('scenario.other'),
  };
  //const numberFormatter = new Intl.NumberFormat('fr-CA');
  const formatMoney = (value) =>
    new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);

  const formatPercent = (value) =>
    `${new Intl.NumberFormat('fr-CA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0)}%`;

  const renewalResult = useMemo(() => {
    if (scenario?.type === 'renewal' && parentScenarioForRenewal && currentProperty) {
      return calculateRenewScenario(scenario, currentProperty, parentScenarioForRenewal, advancedExpenses);
    }
    return null;
  }, [scenario, currentProperty, parentScenarioForRenewal, advancedExpenses]);

  const reportProperty = useMemo(() => {
    if (!scenario) return currentProperty;
    if (renewalResult) {
      return { ...renewalResult.analysisProperty, ...renewalResult.combinedFinancing };
    }
    // Owned "Situation actuelle": property form is source of truth for in-place / none financing.
    // Do not let a stale scenario.financing snapshot override payment, rate, or balance.
    if (isOwnedDeal(currentProperty) && scenario.type === 'initialFinancing') {
      return { ...currentProperty, ...(scenario.acquisitionCosts || {}) };
    }
    const merged = { ...currentProperty, ...(scenario.financing || {}), ...(scenario.acquisitionCosts || {}) };
    return merged;
  }, [currentProperty, scenario, renewalResult]);

  const financingTypeLabel = useMemo(() => {
    const ft = reportProperty.financingType;
    if (ft === 'conventional') return t('propertyReport.financingType.conventional');
    if (ft === 'cmhc') return t('propertyReport.financingType.cmhc');
    if (ft === 'cmhc_aph') return `${t('propertyReport.financingType.cmhc_aph')} (${reportProperty.aphPoints ?? ''} pts)`;
    if (ft === 'private') return t('propertyReport.financingType.private');
    if (ft === 'assumption') return t('propertyReport.financingType.assumption');
    if (ft === 'existing') return t('propertyReport.financingType.existing');
    if (ft === 'none') return t('propertyReport.financingType.none');
    return ft ? String(ft) : '—';
  }, [reportProperty.financingType, reportProperty.aphPoints, t]);

  const fullAddress = [
    reportProperty.address,
    reportProperty.city,
    reportProperty.province,
    reportProperty.postalCode,
  ]
    .filter(Boolean)
    .join(', ');
  const ownedDeal = isOwnedDeal(reportProperty);
  const inPlaceLoan = isInPlaceLoanFinancing(reportProperty.financingType);
  const zeroDebt = isZeroDebtFinancing(reportProperty.financingType);

  const reportAnalysis = useMemo(
    () =>
      scenario
        ? renewalResult
          ? renewalResult.analysis
          : calculateRentability(reportProperty, advancedExpenses)
        : baseAnalysis,
    [scenario, reportProperty, advancedExpenses, baseAnalysis, renewalResult]
  );

  const averageRentPerDoor =
    ((parseFloat(reportProperty.annualRent) || 0) /
      (parseInt(reportProperty.numberOfUnits) || 1)) /
    12;

  const [returnYears, setReturnYears] = useState(5);
  const [returnMode, setReturnMode] = useState('simple');
  const [returnAssumptions, setReturnAssumptions] = useState(() => ({
    revenue: { ...DEFAULT_RETURN_ASSUMPTIONS.revenue },
    expense: { ...DEFAULT_RETURN_ASSUMPTIONS.expense },
    value: { ...DEFAULT_RETURN_ASSUMPTIONS.value },
  }));
  const [advancedSchedule, setAdvancedSchedule] = useState(() =>
    createEmptyAdvancedSchedule(5),
  );
  const [returnProjections, setReturnProjections] = useState([]);
  const [selectedProjectionId, setSelectedProjectionId] = useState('');
  const [loadedProjectionMeta, setLoadedProjectionMeta] = useState(null);
  const [subScenarios, setSubScenarios] = useState([]);
  const [selectedSubScenarioId, setSelectedSubScenarioId] = useState('');
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfPreparedFor, setPdfPreparedFor] = useState('');
  const [pdfIncludeSubScenario, setPdfIncludeSubScenario] = useState(false);
  const [pdfSubScenarioId, setPdfSubScenarioId] = useState('');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const selectedSubScenario = useMemo(
    () => subScenarios.find((s) => s.id === selectedSubScenarioId),
    [subScenarios, selectedSubScenarioId]
  );
  const subScenarioProperty = useMemo(() => {
    if (!selectedSubScenario) return null;
    return {
      ...currentProperty,
      ...selectedSubScenario.financing,
      ...selectedSubScenario.acquisitionCosts,
    };
  }, [currentProperty, selectedSubScenario]);
  const manualAssumptionsRef = useRef({
    ...DEFAULT_RETURN_ASSUMPTIONS,
    revenue: { ...DEFAULT_RETURN_ASSUMPTIONS.revenue },
    expense: { ...DEFAULT_RETURN_ASSUMPTIONS.expense },
    value: { ...DEFAULT_RETURN_ASSUMPTIONS.value },
  });
  const previousSubScenarioRef = useRef(null);
  const subScenarioAnalysis = useMemo(() => {
    if (!selectedSubScenario) return null;
    if (selectedSubScenario.type === 'refinancing') {
      const parentScenario =
        scenario?.id === selectedSubScenario.parentScenarioId
          ? scenario
          : subScenarios.find((s) => s.id === selectedSubScenario.parentScenarioId);
      const { analysis, equityWithdrawal, analysisProperty } =
        calculateFutureScenario(
          selectedSubScenario,
          currentProperty,
          parentScenario,
          advancedExpenses,
        );
      return analysis
        ? { analysis, equityWithdrawal, analysisProperty }
        : null;
    } else if (selectedSubScenario.type === 'optimization') {
      const parentScenario =
        (scenario &&
          (scenario.id === selectedSubScenario.parentScenarioId ||
            scenario.parentScenarioId === selectedSubScenario.parentScenarioId))
          ? scenario
          : subScenarios.find(
              (sc) => sc.id === selectedSubScenario.parentScenarioId,
            );
      const { analysis, equityWithdrawal, analysisProperty } =
        calculateOptimisationScenario(
          selectedSubScenario,
          currentProperty,
          parentScenario,
          advancedExpenses,
        );
      return analysis
        ? { analysis, equityWithdrawal, analysisProperty }
        : null;
    } else if (selectedSubScenario.type === 'renewal') {
      const parentScenario =
        scenario?.id === selectedSubScenario.parentScenarioId
          ? scenario
          : subScenarios.find((s) => s.id === selectedSubScenario.parentScenarioId);
      const { analysis } = calculateRenewScenario(
        selectedSubScenario,
        currentProperty,
        parentScenario,
        advancedExpenses,
      );
      return analysis;
    }
    return null;
  }, [
    selectedSubScenario,
    currentProperty,
    reportAnalysis,
    advancedExpenses,
    scenario,
    subScenarios,
  ]);
  const subScenarioPropertyForList = useMemo(() => {
    if (!selectedSubScenario || !subScenarioAnalysis) return subScenarioProperty;
    if (selectedSubScenario.type === 'renewal') {
      const parent =
        scenario?.id === selectedSubScenario.parentScenarioId
          ? scenario
          : subScenarios.find((s) => s.id === selectedSubScenario.parentScenarioId);
      const { analysisProperty, combinedFinancing } = calculateRenewScenario(
        selectedSubScenario,
        currentProperty,
        parent,
        advancedExpenses,
      );
      return { ...analysisProperty, ...combinedFinancing };
    }
    if (
      (selectedSubScenario.type === 'refinancing' ||
        selectedSubScenario.type === 'optimization') &&
      subScenarioAnalysis.analysisProperty
    ) {
      return {
        ...subScenarioAnalysis.analysisProperty,
        ...selectedSubScenario.financing,
        ...selectedSubScenario.acquisitionCosts,
      };
    }
    return subScenarioProperty;
  }, [
    selectedSubScenario,
    subScenarioAnalysis,
    subScenarioProperty,
    scenario,
    subScenarios,
    currentProperty,
    advancedExpenses,
  ]);
  const effectiveSubScenarioAnalysis = subScenarioAnalysis?.analysis ?? subScenarioAnalysis;

  const hypothesisBases = useMemo(
    () => ({
      baseRevenue: reportAnalysis?.effectiveGrossRevenue || 0,
      baseGrossRevenue: reportAnalysis?.totalGrossRevenue || 0,
      baseExpense: reportAnalysis?.operatingExpensesTotal || 0,
      purchasePrice: parseFloat(reportProperty?.purchasePrice) || 0,
    }),
    [reportAnalysis, reportProperty?.purchasePrice],
  );

  const resolvedRates = useMemo(
    () =>
      resolveAssumptionRates({
        bases: hypothesisBases,
        assumptions: returnAssumptions,
        years: returnYears,
      }),
    [hypothesisBases, returnAssumptions, returnYears],
  );

  const advancedYearProfile = useMemo(
    () =>
      returnMode === 'advanced'
        ? resolveAdvancedYearProfile(hypothesisBases, advancedSchedule)
        : null,
    [returnMode, hypothesisBases, advancedSchedule],
  );

  const returnHints = useMemo(
    () =>
      computeDisplayHints({
        bases: hypothesisBases,
        assumptions: returnAssumptions,
        years: returnYears,
      }),
    [hypothesisBases, returnAssumptions, returnYears],
  );

  const {
    totalReturn: multiYearReturn,
    annualizedReturn: multiYearAnnualized,
    internalRateOfReturn: multiYearIRR,
  } = useMemo(() => {
    const yearProfile =
      returnMode === 'advanced' && advancedYearProfile
        ? {
            revenue: advancedYearProfile.revenue,
            expenses: advancedYearProfile.expenses,
            propertyValueAtYear: advancedYearProfile.propertyValueAtYear,
            values: advancedYearProfile.values,
          }
        : null;
    return calculateReturnAfterYears(
      reportProperty,
      reportAnalysis,
      returnYears,
      resolvedRates.incomeRate,
      resolvedRates.expenseRate,
      resolvedRates.valueRate,
      selectedSubScenario,
      effectiveSubScenarioAnalysis,
      yearProfile,
    );
  }, [
    reportProperty,
    reportAnalysis,
    returnYears,
    resolvedRates,
    selectedSubScenario,
    effectiveSubScenarioAnalysis,
    returnMode,
    advancedYearProfile,
  ]);

  const [editingScenario, setEditingScenario] = useState(null);

  const reportRef = useRef(null);
  const newScenarioPanelRef = useRef(null);

  const expandedClientScenarioAnalysis = useMemo(() => {
    if (!expandedClientScenario || !clientScenarioShareData) return null;
    const { property, baseScenarios, shareScenarios } = clientScenarioShareData;
    const sc = expandedClientScenario;
    const allScenarios = [...baseScenarios, ...shareScenarios];
    let parent = sc.parentScenarioId
      ? allScenarios.find((s) => s.id === sc.parentScenarioId)
      : allScenarios.find((s) => s.type === 'initialFinancing');
    if (sc.type === 'renewal') {
      const initialAmort = parent?.financing?.amortization ?? property?.amortization ?? 25;
      const initialTerm = parent?.financing?.term ?? sc.financing?.term ?? 0;
      if (!parent || parent.financing?.amortization == null || parent.financing?.term == null) {
        parent = {
          ...(parent || {}),
          financing: {
            ...(parent?.financing || {}),
            amortization: initialAmort,
            term: initialTerm,
          },
        };
      }
      const { analysis, analysisProperty, combinedFinancing } = calculateRenewScenario(sc, property, parent, advancedExpenses);
      return {
        analysis,
        property: { ...analysisProperty, ...combinedFinancing },
        combinedFinancing,
      };
    }
    if (sc.type === 'refinancing') {
      const { analysis, analysisProperty } = calculateFutureScenario(sc, property, parent, advancedExpenses);
      return { analysis, property: analysisProperty || property };
    }
    if (sc.type === 'optimization') {
      const { analysis, analysisProperty } = calculateOptimisationScenario(
        sc,
        property,
        parent,
        advancedExpenses,
      );
      return { analysis, property: analysisProperty || property };
    }
    return null;
  }, [expandedClientScenario, clientScenarioShareData, advancedExpenses]);

  const isClientScenarioExpanded = (sc) =>
    expandedClientScenario && expandedClientScenario.shareToken === sc.shareToken && expandedClientScenario.id === sc.id;

  const baseScenarioId = scenario ? scenario.parentScenarioId || scenario.id : null;

  // Only show sub-scenarios from shares that are actual children of the current scenario (not the parent/main scenario).
  const sharedSubScenariosFromShares = useMemo(() => {
    if (!sharedScenariosFromClients?.length || !baseScenarioId) return [];
    return sharedScenariosFromClients.filter((sc) => sc.parentScenarioId === baseScenarioId);
  }, [sharedScenariosFromClients, baseScenarioId]);

  useEffect(() => {
    if (!currentProperty?.id) return;
    const loadScenarios = (scs) => {
      const parentFiltered = baseScenarioId
        ? scs.filter((sc) => sc.parentScenarioId === baseScenarioId)
        : scs.filter((sc) => sc.type !== 'renewal');
      const financingSubs = parentFiltered.filter(
        (sc) => sc.type !== 'returnProjection',
      );
      const projections = parentFiltered.filter(
        (sc) => sc.type === 'returnProjection',
      );
      setSubScenarios(financingSubs);
      setReturnProjections(projections);
      setSelectedSubScenarioId((currentSelectedId) =>
        financingSubs.some((sc) => sc.id === currentSelectedId)
          ? currentSelectedId
          : '',
      );
      setSelectedProjectionId((currentId) =>
        projections.some((p) => p.id === currentId) ? currentId : '',
      );
    };
    let unsub;
    if (shareToken) {
      unsub = getShareScenarios(shareToken, (list) => {
        const filteredByCreator = shareFilterByCreatorUid
          ? list.filter((s) => !s.createdByUid || s.createdByUid === shareFilterByCreatorUid)
          : list;
        const full = [...(baseScenariosProp || []), ...filteredByCreator];
        loadScenarios(full);
      });
    } else {
      unsub = getScenarios(currentProperty.id, loadScenarios);
    }
    return () => {
      const u = unsub;
      queueMicrotask(() => u?.());
    };
  }, [currentProperty?.id, baseScenarioId, shareToken, shareFilterByCreatorUid, baseScenariosProp]);

  useEffect(() => {
    const previousSubScenario = previousSubScenarioRef.current;
    const baseline = manualAssumptionsRef.current;

    if (
      returnMode === 'simple' &&
      selectedSubScenario?.type === 'refinancing'
    ) {
      setReturnAssumptions(assumptionsFromRefinancingSubScenario(selectedSubScenario));
    } else if (selectedSubScenario?.type === 'optimization') {
      setReturnAssumptions({
        revenue: { ...baseline.revenue },
        expense: { ...baseline.expense },
        value: { ...baseline.value },
      });
    } else if (!selectedSubScenario && previousSubScenario) {
      setReturnAssumptions({
        revenue: { ...baseline.revenue },
        expense: { ...baseline.expense },
        value: { ...baseline.value },
      });
    }

    previousSubScenarioRef.current = selectedSubScenario;
  }, [selectedSubScenario, returnMode]);

  const handleReturnAssumptionsChange = (next) => {
    setReturnAssumptions(next);
    if (!selectedSubScenario) {
      manualAssumptionsRef.current = {
        revenue: { ...next.revenue },
        expense: { ...next.expense },
        value: { ...next.value },
      };
    }
    setSelectedProjectionId('');
    setLoadedProjectionMeta(null);
  };

  const handleLoadReturnProjection = (proj) => {
    const mode = proj.mode === 'advanced' ? 'advanced' : 'simple';
    const years = parseInt(proj.horizonYears, 10) || 5;
    if (mode === 'advanced' && !proj.advancedSchedule) return;
    if (mode === 'simple' && !proj.assumptions) return;
    setReturnYears(years);
    setReturnMode(mode);
    if (mode === 'advanced' && proj.advancedSchedule) {
      setAdvancedSchedule(resizeAdvancedSchedule(proj.advancedSchedule, years));
    } else if (proj.assumptions) {
      setReturnAssumptions({
        revenue: { ...proj.assumptions.revenue },
        expense: { ...proj.assumptions.expense },
        value: { ...proj.assumptions.value },
      });
      if (!selectedSubScenario) {
        manualAssumptionsRef.current = {
          revenue: { ...proj.assumptions.revenue },
          expense: { ...proj.assumptions.expense },
          value: { ...proj.assumptions.value },
        };
      }
    }
    setSelectedProjectionId(proj.id);
    setLoadedProjectionMeta({
      title: proj.title,
      outlook: proj.outlook,
    });
  };

  const handleSaveReturnProjection = async ({
    title,
    outlook,
    horizonYears,
    mode,
    assumptions,
    advancedSchedule: scheduleToSave,
  }) => {
    if (!baseScenarioId) throw new Error('No base scenario');
    const payload = {
      type: 'returnProjection',
      parentScenarioId: baseScenarioId,
      title,
      outlook,
      horizonYears,
      mode: mode || 'simple',
      ...(mode === 'advanced'
        ? { advancedSchedule: scheduleToSave }
        : { assumptions }),
    };
    if (shareToken) {
      await saveShareScenario(shareToken, payload, shareCreatorInfo);
    } else {
      await saveScenario(
        currentProperty.id,
        payload,
        currentUser?.uid ?? undefined,
      );
    }
  };

  const handleDeleteReturnProjection = async (id) => {
    if (shareToken) {
      await deleteShareScenario(shareToken, id);
    } else {
      await deleteScenario(currentProperty.id, id);
    }
  };

  const handleOpenPdfModal = () => {
    setPdfModalOpen(true);
    setPdfError(null);
    setPdfPreparedFor(String(reportProperty.clientName || '').trim());
    setPdfIncludeSubScenario(false);
    setPdfSubScenarioId(selectedSubScenarioId || '');
  };

  const handleConfirmPdf = async () => {
    if (pdfIncludeSubScenario) {
      if (
        !pdfSubScenarioId ||
        !subScenarios.some((s) => s.id === pdfSubScenarioId)
      ) {
        setPdfError(t('propertyReport.pdfSubScenarioPick'));
        return;
      }
    }
    setPdfGenerating(true);
    setPdfError(null);
    try {
      const sub = pdfIncludeSubScenario
        ? subScenarios.find((s) => s.id === pdfSubScenarioId)
        : null;
      const bundle = sub
        ? getSubScenarioPdfBundle(
            sub,
            currentProperty,
            scenario,
            subScenarios,
            advancedExpenses,
          )
        : null;
      if (pdfIncludeSubScenario && !bundle) {
        setPdfError(t('propertyReport.pdfSubScenarioInvalid'));
        setPdfGenerating(false);
        return;
      }
      const growth = sub ? subScenarioGrowthStrings(sub, formatPercent) : null;
      const scenarioPdf =
        bundle && sub
          ? {
              scenarioName: sub.title || typeLabels[sub.type] || '—',
              scenarioDescription: '',
              typeLabel: typeLabels[sub.type] || sub.type,
              projectionYears: returnYears,
              incomeGrowth: growth.income,
              expenseGrowth: growth.expense,
              valueAppreciation: growth.value,
              analysis: bundle.analysis,
              property: bundle.property,
              equityWithdrawal: bundle.equityWithdrawal ?? null,
            }
          : null;

      const logoSrc =
        typeof window !== 'undefined'
          ? `${window.location.origin}/rentalyzer-logo.png`
          : '';

      const buildingImageDataUrl = await resolveImageForPdf(
        getPrimaryBuildingImageUrl(reportProperty) || '',
      );

      const doc = (
        <RentalizerPdfDocument
          title={ownedDeal ? t('propertyReport.titleOwned') : t('propertyReport.title')}
          property={reportProperty}
          analysis={reportAnalysis}
          futureReturns={{
            years: returnYears,
            totalReturn: multiYearReturn,
            annualizedReturn: multiYearAnnualized,
            irr: multiYearIRR,
            mode: returnMode,
            assumptionSummary:
              returnMode === 'simple'
                ? buildAssumptionSummaryLines(
                    returnAssumptions,
                    returnYears,
                    language === 'en' ? 'en-CA' : 'fr-CA',
                  )
                : [],
            advancedScheduleRows:
              returnMode === 'advanced'
                ? buildAdvancedSchedulePdfRows(
                    hypothesisBases,
                    advancedSchedule,
                    language === 'en' ? 'en-CA' : 'fr-CA',
                  )
                : [],
            loadedProjectionTitle: loadedProjectionMeta?.title,
            outlookLabel: loadedProjectionMeta?.outlook
              ? t(
                  `propertyReport.futureReturns.outlook.${loadedProjectionMeta.outlook}`,
                )
              : undefined,
          }}
          marketParamsVersion={marketParamsVersion}
          preparedFor={pdfPreparedFor.trim()}
          buildingImageUrl={buildingImageDataUrl}
          logoSrc={logoSrc}
          language={language === 'en' ? 'en' : 'fr'}
          showScenarioPage={Boolean(pdfIncludeSubScenario && bundle)}
          scenarioPdf={
            pdfIncludeSubScenario && bundle ? scenarioPdf : null
          }
        />
      );

      const blob = await pdf(doc).toBlob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `rapport-${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(href);
      setPdfModalOpen(false);
    } catch (err) {
      console.error(err);
      setPdfError(t('propertyReport.pdfGenerateError'));
    } finally {
      setPdfGenerating(false);
    }
  };

  useRegisterTopEscapeClose(
    () => {
      if (!pdfGenerating) setPdfModalOpen(false);
    },
    pdfModalOpen,
  );

  const isCreatingNewScenario =
    Boolean(editingScenario) &&
    !editingScenario.id &&
    editingScenario.parentScenarioId === baseScenarioId;
  const showStandaloneScenarioForm = Boolean(
    editingScenario &&
      (!editingScenario.id || editingScenario.parentScenarioId !== baseScenarioId),
  );

  useEffect(() => {
    if (!showStandaloneScenarioForm) return;
    newScenarioPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [showStandaloneScenarioForm, editingScenario?.type]);

  const renderScenarioForm = (opts = {}) => {
    if (!editingScenario) return null;
    if (!editingScenario.type) {
      const scenarioTypes = [
        { type: 'refinancing', label: t('scenario.refinancing') },
        { type: 'renewal', label: t('scenario.renewal') },
        { type: 'optimization', label: t('scenario.optimization') },
      ];
      return (
        <div
          className="w-full rounded-xl border border-blue-200 bg-blue-50 p-4"
          data-onboarding="report-scenario-type-picker"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {t('propertyReport.chooseScenarioType')}
            </h3>
            <button
              type="button"
              onClick={() => setEditingScenario(null)}
              className="shrink-0 text-sm font-medium text-gray-600 hover:text-gray-900 px-2 py-1"
            >
              {t('cancel')}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {scenarioTypes.map(({ type, label }) => (
              <button
                key={type}
                type="button"
                onClick={() => setEditingScenario({ ...editingScenario, type })}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base font-medium"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    const formProps = {
      propertyId: currentProperty.id,
      property: currentProperty,
      advancedExpenses,
      onSaved: () => setEditingScenario(null),
      onBack: () => setEditingScenario(null),
      initialScenario: editingScenario,
      shareToken: shareToken || undefined,
      shareFilterByCreatorUid: shareFilterByCreatorUid || undefined,
      shareCreatorInfo: shareCreatorInfo || undefined,
      baseScenarios: shareToken ? baseScenariosProp || [] : undefined,
      initialViewMode: 'edit',
      creatorUid: !shareToken ? currentUser?.uid : undefined,
      ...opts,
    };
    const formComponents = {
      refinancing: FutureScenarioForm,
      renewal: RenewScenarioForm,
      optimization: OptimisationScenarioForm,
    };
    const ScenarioFormComponent = formComponents[editingScenario.type];
    return <ScenarioFormComponent {...formProps} type={editingScenario.type} />;
  };
  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 overflow-x-hidden">
      <div className="max-w-6xl mx-auto">
        <div
          ref={reportRef}
          className="bg-white rounded-lg shadow-lg p-4 sm:p-6"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold min-w-0" data-onboarding="report-header">
              {ownedDeal ? t('propertyReport.titleOwned') : t('propertyReport.title')}
            </h2>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full sm:w-auto sm:justify-end shrink-0">
              {scenario && !scenario.shareToken && !readOnly && (
                <button
                  type="button"
                  data-onboarding="report-edit"
                  onClick={() => setCurrentStep('scenario')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('edit')}
                </button>
              )}
              {scenario ? (
                <button
                  onClick={() => setCurrentStep('dashboard')}
                  className="text-gray-600 hover:text-gray-800"
                >
                  ← {t('back')}
                </button>
              ) : (
                <button
                  onClick={onSave}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {t('save')}
                </button>
              )}
            </div>
          </div>

          <div
            className={`bg-gradient-to-r from-blue-50 to-green-50 rounded-lg mb-6 ${
              ownedDeal ? 'pt-4 sm:pt-6' : 'p-4 sm:p-6'
            }`}
            data-onboarding="report-entered-info"
          >
            <h3
              className={`text-xl font-semibold ${
                ownedDeal ? 'px-4 sm:px-6 mb-5' : 'mb-2'
              }`}
            >
              {fullAddress || t('propertyReport.analyzingProperty')}
            </h3>
            {ownedDeal ? (
              /* Same 3-col tracks as KeyIndicators; px-4 sm:px-6 matches address inset on every column */
              <div className="space-y-4 text-sm pb-4 sm:pb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.estimatedMarketValue')}:</span>
                    <div className="font-semibold">
                      {formatMoney(parseFloat(reportProperty.purchasePrice) || 0)}
                    </div>
                  </div>
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.numberOfUnits')}:</span>
                    <div className="font-semibold">{reportProperty.numberOfUnits || 'N/A'}</div>
                  </div>
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.valuePerDoor')}:</span>
                    <div className="font-semibold">
                      {formatMoney(Math.round(reportAnalysis.pricePerUnit || 0))}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.totalRevenue')}:</span>
                    <div className="font-semibold">
                      {formatMoney(reportAnalysis.totalGrossRevenue)}
                    </div>
                  </div>
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.totalExpenses')}:</span>
                    <div className="font-semibold text-red-600">
                      {formatMoney(reportAnalysis.totalExpenses)}
                    </div>
                  </div>
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.cashFlow')}:</span>
                    <div className={`font-semibold ${reportAnalysis.cashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatMoney(reportAnalysis.cashFlow)}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.financing')}:</span>
                    <div className="font-semibold">{financingTypeLabel}</div>
                  </div>
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.outstandingLoan')}:</span>
                    <div className="font-semibold">
                      {formatMoney(reportAnalysis.maxLoanAmount)}
                    </div>
                  </div>
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.interestRate')}:</span>
                    <div className="font-semibold">
                      {zeroDebt
                        ? '—'
                        : formatPercent(reportAnalysis.mortgageRate)}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.remainingAmortMonths')}:</span>
                    <div className="font-semibold">
                      {inPlaceLoan && reportAnalysis.assumptionDerivedRemainingMonths != null
                        ? new Intl.NumberFormat(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 1,
                          }).format(reportAnalysis.assumptionDerivedRemainingMonths)
                        : '—'}
                    </div>
                  </div>
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.monthlyPayment')}:</span>
                    <div className="font-semibold">
                      {zeroDebt
                        ? '—'
                        : formatMoney(reportAnalysis.monthlyPayment)}
                    </div>
                  </div>
                  <div className="px-4 sm:px-6">
                    <span className="text-gray-600">{t('propertyReport.netEquity')}:</span>
                    <div className="font-semibold">
                      {formatMoney(reportAnalysis.equity ?? reportAnalysis.downPayment)}
                    </div>
                  </div>
                </div>
              </div>
            ) : advancedExpenses ? (
              <div className="space-y-4 text-sm">
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <span className="text-gray-600">{t('propertyReport.askingPrice')}:</span>
                      <div className="font-semibold">
                        {formatMoney(parseFloat(reportProperty.askingPrice) || 0)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('propertyReport.purchasePrice')}:</span>
                      <div className="font-semibold">
                        {formatMoney(parseFloat(reportProperty.purchasePrice) || 0)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('propertyReport.numberOfUnits')}:</span>
                      <div className="font-semibold">{reportProperty.numberOfUnits || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('propertyReport.pricePerDoor')}:</span>
                      <div className="font-semibold">
                        {formatMoney(Math.round(reportAnalysis.pricePerUnit || 0))}
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    {reportProperty.financingType !== 'private' && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.totalRevenue')}:</span>
                      <div className="font-semibold">
                        {formatMoney(reportAnalysis.totalGrossRevenue)}
                      </div>
                    </div>
                    )}
                    {reportProperty.financingType !== 'private' && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.totalExpenses')}:</span>
                      <div className="font-semibold text-red-600">
                        {formatMoney(reportAnalysis.totalExpenses)}
                      </div>
                    </div>
                    )}
                    {reportProperty.financingType !== 'private' && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.debtServiceYear1')}:</span>
                      <div className="font-semibold text-red-600">
                        {formatMoney(reportAnalysis.annualDebtService)}
                      </div>
                    </div>
                    )}
                    {reportProperty.financingType !== 'private' && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.cashFlow')}:</span>
                      <div className={`font-semibold ${reportAnalysis.cashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatMoney(reportAnalysis.cashFlow)}
                      </div>
                    </div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    {reportProperty.financingType !== 'private' && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.financing')}:</span>
                      <div className="font-semibold">{financingTypeLabel}</div>
                    </div>
                    )}
                    {(reportAnalysis.mortgageRate != null && reportAnalysis.mortgageRate !== '') && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.interestRate')}:</span>
                      <div className="font-semibold">{formatPercent(reportAnalysis.mortgageRate)}</div>
                    </div>
                    )}
                    {(reportProperty.amortization != null && reportProperty.amortization !== '') && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.amortizationYears')}:</span>
                      <div className="font-semibold">{reportProperty.amortization} {t('propertyReport.years')}</div>
                    </div>
                    )}
                    {(reportProperty.term != null && reportProperty.term !== '') && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.financingTerm')}:</span>
                      <div className="font-semibold">{reportProperty.term} {t('propertyReport.years')}</div>
                    </div>
                    )}
                    {reportProperty.financingType !== 'private' && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.maxLoan')}:</span>
                      <div className="font-semibold">{formatMoney(reportAnalysis.maxLoanAmount)}</div>
                    </div>
                    )}
                    {reportProperty.financingType === 'private' && (
                      <div>
                      <span className="text-gray-600">{t('propertyReport.loanGranted')}:</span>
                      <div className="font-semibold">{formatMoney(reportAnalysis.maxLoanAmount)}</div>
                    </div>
                    )}
                    {(reportProperty.financingType === 'private' ||
                      reportProperty.financingType === 'assumption') && (
                      <div>
                        <span className="text-gray-600">{t('propertyReport.ltv')}:</span>
                        <div className="font-semibold">{formatPercent(reportAnalysis.loanValueRatio)}</div>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">{t('propertyReport.downPayment')}:</span>
                      <div className="font-semibold">{formatMoney(reportAnalysis.downPayment)}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('propertyReport.totalInvestment')}:</span>
                      <div className="font-semibold">{formatMoney(reportAnalysis.totalInvestment)}</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 text-sm">
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <span className="text-gray-600">{t('propertyReport.purchasePrice')}:</span>
                      <div className="font-semibold">
                        {formatMoney(parseFloat(reportProperty.purchasePrice) || 0)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('propertyReport.numberOfUnits')}:</span>
                      <div className="font-semibold">{reportProperty.numberOfUnits || 'N/A'}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('propertyReport.pricePerDoor')}:</span>
                      <div className="font-semibold">
                        {formatMoney(Math.round(reportAnalysis.pricePerUnit || 0))}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('propertyReport.averageRentPerDoor')}:</span>
                      <div className="font-semibold">{formatMoney(averageRentPerDoor)}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    {reportProperty.financingType !== 'private' && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.financing')}:</span>
                      <div className="font-semibold">{financingTypeLabel}</div>
                    </div>
                    )}
                    {(reportAnalysis.mortgageRate != null && reportAnalysis.mortgageRate !== '') && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.interestRate')}:</span>
                      <div className="font-semibold">{formatPercent(reportAnalysis.mortgageRate)}</div>
                    </div>
                    )}
                    {(reportProperty.amortization != null && reportProperty.amortization !== '') && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.amortizationYears')}:</span>
                      <div className="font-semibold">{reportProperty.amortization} {t('propertyReport.years')}</div>
                    </div>
                    )}
                    {(reportProperty.term != null && reportProperty.term !== '') && (
                    <div>
                      <span className="text-gray-600">{t('propertyReport.financingTerm')}:</span>
                      <div className="font-semibold">{reportProperty.term} {t('propertyReport.years')}</div>
                    </div>
                    )}
                    <div>
                      <span className="text-gray-600">
                        {inPlaceLoan ? t('propertyReport.outstandingLoan') : t('propertyReport.maxLoan')}:
                      </span>
                      <div className="font-semibold">{formatMoney(reportAnalysis.maxLoanAmount)}</div>
                    </div>
                    {(reportProperty.financingType === 'private' ||
                      reportProperty.financingType === 'assumption') && (
                      <div>
                        <span className="text-gray-600">{t('propertyReport.ltv')}:</span>
                        <div className="font-semibold">{formatPercent(reportAnalysis.loanValueRatio)}</div>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">{t('propertyReport.downPayment')}:</span>
                      <div className="font-semibold">{formatMoney(reportAnalysis.downPayment)}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">{t('propertyReport.totalInvestment')}:</span>
                      <div className="font-semibold">{formatMoney(reportAnalysis.totalInvestment)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div data-onboarding="report-indicators">
          <KeyIndicators
            analysis={reportAnalysis}
            variant={
              reportProperty.financingType === "private"
                ? "private"
                : ownedDeal
                  ? "owned"
                  : "acquisition"
            }
            exclude={scenario?.type === 'renewal' ? ['mrb', 'mrn', 'tga'] : []}
          />
          </div>
          {/* Conformité SCHL / APH — masqué
          <SchlComplianceSection analysis={reportAnalysis} property={reportProperty} />
          */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div data-onboarding="report-financial-summary">
            <FinancialSummary analysis={reportAnalysis} advancedExpenses={advancedExpenses} />
            </div>
            <div data-onboarding="report-financing-summary">
            <FinancingSummary
              analysis={reportAnalysis}
              currentProperty={reportProperty}
              financing={reportProperty}
              scenarioType="initialFinancing"
            />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center items-center gap-3 sm:gap-4 mb-8">
            {!zeroDebt && (
            <button
              type="button"
              data-onboarding="report-amortization"
              onClick={() =>
                onViewAmortization(
                  {
                    ...reportProperty,
                    appreciationRate: resolvedRates.valueRate,
                    ...(inPlaceLoan &&
                    reportAnalysis.assumptionDerivedRemainingMonths != null
                      ? {
                          amortization:
                            reportAnalysis.assumptionDerivedRemainingMonths / 12,
                        }
                      : {}),
                  },
                  reportAnalysis,
                  selectedSubScenario,
                  subScenarioAnalysis?.analysis ?? subScenarioAnalysis,
                )
              }
              className="order-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('propertyReport.amortization')}
            </button>
            )}
            {!readOnly && allowSubScenariosEdit && (
              <button
                type="button"
                data-onboarding="report-new-scenario"
                aria-expanded={isCreatingNewScenario}
                aria-controls="new-scenario-panel"
                onClick={() => {
                  if (isCreatingNewScenario) {
                    setEditingScenario(null);
                    return;
                  }
                  setEditingScenario({ parentScenarioId: baseScenarioId });
                }}
                className={`order-2 px-4 py-2 rounded-lg ${
                  isCreatingNewScenario
                    ? 'bg-blue-800 text-white ring-2 ring-blue-300'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {t('propertyReport.newScenario')}
              </button>
            )}
            {!readOnly && (
              <button
                type="button"
                data-onboarding="report-pdf"
                onClick={handleOpenPdfModal}
                className="order-4 sm:order-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {t('propertyReport.generatePdf')}
              </button>
            )}
            {showStandaloneScenarioForm && !editingScenario.type && (
              <div
                id="new-scenario-panel"
                ref={newScenarioPanelRef}
                className="order-3 sm:order-4 w-full basis-full"
              >
                {renderScenarioForm()}
              </div>
            )}
          </div>

          {showStandaloneScenarioForm && editingScenario.type ? (
            <div id="new-scenario-panel" ref={newScenarioPanelRef} className="mb-8">
              {renderScenarioForm()}
            </div>
          ) : null}

          <div data-onboarding="report-future-returns">
          <FutureReturnsSection
            returnYears={returnYears}
            onReturnYearsChange={(y) => {
              setReturnYears(y);
              setAdvancedSchedule((prev) => resizeAdvancedSchedule(prev, y));
              setSelectedProjectionId('');
              setLoadedProjectionMeta(null);
            }}
            returnMode={returnMode}
            onReturnModeChange={(mode) => {
              setReturnMode(mode);
              setSelectedProjectionId('');
              setLoadedProjectionMeta(null);
              if (mode === 'advanced') {
                setAdvancedSchedule((prev) =>
                  resizeAdvancedSchedule(prev, returnYears),
                );
              }
            }}
            assumptions={returnAssumptions}
            onAssumptionsChange={handleReturnAssumptionsChange}
            advancedSchedule={advancedSchedule}
            onAdvancedScheduleChange={(sched) => {
              setAdvancedSchedule(sched);
              setSelectedProjectionId('');
              setLoadedProjectionMeta(null);
            }}
            hypothesisBases={hypothesisBases}
            onPrefillAdvancedFromSimple={() => {
              const filled = buildAdvancedScheduleFromSimple(
                hypothesisBases,
                returnAssumptions,
                returnYears,
              );
              setAdvancedSchedule(filled);
              return filled;
            }}
            onResetAdvancedSchedule={() => {
              const empty = createEmptyAdvancedSchedule(returnYears);
              setAdvancedSchedule(empty);
              return empty;
            }}
            hints={returnHints}
            hasSubScenario={Boolean(selectedSubScenarioId)}
            readOnly={readOnly}
            subScenarios={subScenarios}
            selectedSubScenarioId={selectedSubScenarioId}
            onSubScenarioChange={(nextId) => {
              setSelectedSubScenarioId(nextId);
              if (nextId) setExpandedClientScenario(null);
            }}
            typeLabels={typeLabels}
            returnProjections={returnProjections}
            selectedProjectionId={selectedProjectionId}
            onLoadProjection={handleLoadReturnProjection}
            onClearProjection={() => {
              setSelectedProjectionId('');
              setLoadedProjectionMeta(null);
            }}
            onSaveProjection={handleSaveReturnProjection}
            onDeleteProjection={handleDeleteReturnProjection}
            multiYearReturn={multiYearReturn}
            multiYearAnnualized={multiYearAnnualized}
            multiYearIRR={multiYearIRR}
            formatPercent={formatPercent}
            t={t}
            language={language}
          />
          </div>

          <div className="mb-8">
            <ScenarioList
              propertyId={currentProperty.id}
              shareToken={shareToken || undefined}
              baseScenarios={shareToken ? baseScenariosProp : undefined}
              shareCreatorInfo={shareToken ? shareCreatorInfo : undefined}
              shareFilterByCreatorUid={shareToken ? shareFilterByCreatorUid : undefined}
              readOnly={readOnly}
              onView={(sc) => setSelectedSubScenarioId(sc.id)}
              onEdit={readOnly ? undefined : (sc) => setEditingScenario(sc)}
              onCloseSubScenario={() => {
                setSelectedSubScenarioId('');
                setEditingScenario(null);
              }}
              excludeTypes={['initialFinancing', 'returnProjection']}
              parentScenarioId={baseScenarioId}
              selectedSubScenarioId={selectedSubScenarioId}
              expandedContent={
                selectedSubScenarioId && subScenarioAnalysis && subScenarioPropertyForList
                  ? {
                      analysis: subScenarioAnalysis?.analysis ?? subScenarioAnalysis,
                      property: subScenarioPropertyForList,
                      financing: selectedSubScenario?.financing
                        ? { ...subScenarioPropertyForList, ...selectedSubScenario.financing }
                        : subScenarioPropertyForList,
                      scenarioType: selectedSubScenario?.type,
                      advancedExpenses,
                      equityWithdrawal: subScenarioAnalysis?.equityWithdrawal,
                      valueGeneratedYear:
                        (selectedSubScenario?.type === 'refinancing' || selectedSubScenario?.type === 'renewal') &&
                        scenario?.financing?.term != null
                          ? (parseInt(scenario.financing.term) || 0) + 1
                          : undefined,
                    }
                  : null
              }
              editingScenarioId={
                editingScenario?.id && editingScenario?.parentScenarioId === baseScenarioId
                  ? editingScenario.id
                  : null
              }
              renderEditForm={
                editingScenario?.id && editingScenario?.parentScenarioId === baseScenarioId
                  ? () => renderScenarioForm({ embeddedInList: true })
                  : undefined
              }
              canEditScenario={!shareToken ? canEditScenario : undefined}
              canDeleteScenario={!shareToken ? canDeleteScenario : undefined}
              creatorUid={!shareToken ? currentUser?.uid : undefined}
            />
            {sharedSubScenariosFromShares.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  {t('building.subScenariosFromShares')}
                </h3>
                <div className="space-y-2">
                  {sharedSubScenariosFromShares.map((sc) => (
                    <div key={`${sc.shareToken}-${sc.id}`} className="rounded-lg border border-indigo-100 overflow-hidden">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-3 bg-indigo-50">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                          <span className="font-medium text-gray-800">{sc.title || t('propertyReport.untitled')}</span>
                          <span className="text-sm text-indigo-700">
                            {t('building.createdBy')} {sc.createdByName || sc.createdByUid || '—'}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const isCurrentlyThis = expandedClientScenario?.shareToken === sc.shareToken && expandedClientScenario?.id === sc.id;
                            const next = isCurrentlyThis ? null : sc;
                            setExpandedClientScenario(next);
                            if (next) setSelectedSubScenarioId('');
                          }}
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                        >
                          {isClientScenarioExpanded(sc) ? t('close') : t('view')}
                        </button>
                      </div>
                      {isClientScenarioExpanded(sc) && expandedClientScenarioAnalysis?.analysis && (
                        <div className="p-4 bg-white border-t border-indigo-100">
                          <KeyIndicators
                            analysis={expandedClientScenarioAnalysis.analysis}
                            variant={
                              expandedClientScenarioAnalysis.property?.financingType === 'private'
                                ? 'private'
                                : isOwnedDeal(expandedClientScenarioAnalysis.property)
                                  ? 'owned'
                                  : 'acquisition'
                            }
                            exclude={sc.type === 'renewal' ? ['mrb', 'mrn', 'tga'] : []}
                          />
                          <div className="grid md:grid-cols-2 gap-6 mt-6">
                            <FinancialSummary
                              analysis={expandedClientScenarioAnalysis.analysis}
                              advancedExpenses={advancedExpenses}
                            />
                            <FinancingSummary
                              analysis={expandedClientScenarioAnalysis.analysis}
                              currentProperty={expandedClientScenarioAnalysis.property}
                              financing={expandedClientScenarioAnalysis.combinedFinancing ?? expandedClientScenarioAnalysis.property}
                              scenarioType={sc.type || 'renewal'}
                            />
                          </div>
                        </div>
                      )}
                      {isClientScenarioExpanded(sc) && !clientScenarioShareData && (
                        <div className="p-4 bg-white border-t border-indigo-100 text-sm text-gray-500">
                          {t('loading')}…
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Synthèse de l'opportunité — masqué
          <DealSummary analysis={reportAnalysis} property={reportProperty} />
          */}
          {/* Résumé exécutif — masqué
          <ExecutiveSummary
            analysis={reportAnalysis}
            currentProperty={reportProperty}
          />
          */}
        </div>
      </div>

      {pdfModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pdf-modal-title"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 id="pdf-modal-title" className="text-lg font-semibold text-gray-900">
              {t('propertyReport.pdfModalTitle')}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('propertyReport.pdfPreparedForLabel')}
              </label>
              <input
                type="text"
                value={pdfPreparedFor}
                onChange={(e) => setPdfPreparedFor(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder={t('propertyReport.pdfPreparedForPlaceholder')}
              />
            </div>
            {subScenarios.length > 0 ? (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pdfIncludeSubScenario}
                    onChange={(e) => setPdfIncludeSubScenario(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  {t('propertyReport.pdfIncludeSubScenario')}
                </label>
                {pdfIncludeSubScenario ? (
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {t('propertyReport.pdfSubScenarioSelect')}
                    </label>
                    <select
                      value={pdfSubScenarioId}
                      onChange={(e) => setPdfSubScenarioId(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="">{t('propertyReport.pdfSubScenarioPlaceholder')}</option>
                      {subScenarios.map((sc) => (
                        <option key={sc.id} value={sc.id}>
                          {sc.title || typeLabels[sc.type] || sc.id}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>
            ) : null}
            {pdfError ? (
              <p className="text-sm text-red-600">{pdfError}</p>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setPdfModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={pdfGenerating}
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmPdf}
                disabled={pdfGenerating}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {pdfGenerating
                  ? t('propertyReport.generatingPdf')
                  : t('propertyReport.pdfModalConfirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PropertyReport;
